// Outlook calendars on Today and the Calendar page, shared by web and mobile.
// Whether an event blocks its time is the database's rule
// (private.event_blocks, carried as `blocks`); nothing here decides it again.

import { formatInstantClock } from './clock.ts';

/** What a calendar's events mean for planning. */
export type CalendarRole = 'off' | 'block' | 'fyi' | 'mixed';

export const CALENDAR_ROLES: readonly CalendarRole[] = ['off', 'block', 'fyi', 'mixed'];

export const CALENDAR_ROLE_LABELS: Record<CalendarRole, string> = {
  off: 'Off',
  block: 'Block',
  fyi: 'FYI',
  mixed: 'Mixed',
};

export const CALENDAR_ROLE_HINTS: Record<CalendarRole, string> = {
  off: 'Not synced',
  block: 'Its events make that time busy',
  fyi: 'Shown, never busy',
  mixed: 'Busy unless you say an event is not',
};

export function isCalendarRole(value: unknown): value is CalendarRole {
  return typeof value === 'string' && (CALENDAR_ROLES as readonly string[]).includes(value);
}

/** A row of calendar_days (the calendar_event_view). */
export interface CalendarEvent {
  id: string;
  calendar_id: string;
  calendar_name: string;
  calendar_color: string | null;
  role: CalendarRole;
  title: string;
  location: string | null;
  /** A timed event's instants; null for an all-day event. */
  starts_at: string | null;
  ends_at: string | null;
  /** An all-day event's days (YYYY-MM-DD, `to` exclusive); null for a timed event. */
  all_day_from: string | null;
  all_day_to: string | null;
  show_as: string | null;
  /** On a Mixed calendar, false when the owner said this event does not block. */
  blocks_override: boolean | null;
  /** Whether the event makes its time busy. */
  blocks: boolean;
}

/** A Mixed calendar's event can be told whether it blocks; no other can. */
export const canOverride = (event: Pick<CalendarEvent, 'role'>): boolean => event.role === 'mixed';

function clock(instant: string, timeZone: string | undefined): string {
  return formatInstantClock(instant, timeZone);
}

function localDay(instant: string, timeZone: string | undefined): string {
  // en-CA formats as YYYY-MM-DD.
  return new Date(instant).toLocaleDateString('en-CA', { timeZone });
}

/**
 * When the event is, as seen on `day` (YYYY-MM-DD in `timeZone`): "All day",
 * "9:00 AM – 10:00 AM", or, for an event that runs past the day's edges,
 * "Until 1:00 AM" / "From 11:00 PM" / "All day" for a day it spans.
 */
export function eventTimeLabel(event: Pick<CalendarEvent, 'starts_at' | 'ends_at'>, day: string, timeZone?: string): string {
  if (!event.starts_at || !event.ends_at) return 'All day';
  const startsToday = localDay(event.starts_at, timeZone) === day;
  const endsToday = localDay(event.ends_at, timeZone) === day;
  if (startsToday && endsToday) return `${clock(event.starts_at, timeZone)} – ${clock(event.ends_at, timeZone)}`;
  if (startsToday) return `From ${clock(event.starts_at, timeZone)}`;
  if (endsToday) return `Until ${clock(event.ends_at, timeZone)}`;
  return 'All day';
}

/** The events on `day` (YYYY-MM-DD in `timeZone`): all-day ones covering it, timed ones overlapping it. */
export function eventsOnDay<T extends Pick<CalendarEvent, 'starts_at' | 'ends_at' | 'all_day_from' | 'all_day_to'>>(
  events: readonly T[],
  day: string,
  timeZone?: string,
): T[] {
  return events.filter((event) => {
    if (event.all_day_from && event.all_day_to) return event.all_day_from <= day && day < event.all_day_to;
    if (!event.starts_at || !event.ends_at) return false;
    const first = localDay(event.starts_at, timeZone);
    const last = localDay(event.ends_at, timeZone);
    // An event ending exactly at midnight does not reach the next day.
    const endsAtMidnight = event.ends_at !== event.starts_at && clock(event.ends_at, timeZone) === '12:00 AM';
    return first <= day && (day < last || (day === last && !endsAtMidnight));
  });
}

/** What the Integrations page says after Microsoft sends the user back (?outlook=...), or null for anything else. */
export function outlookReturnMessage(outcome: string | null): { ok: boolean; message: string } | null {
  switch (outcome) {
    case 'connected':
      return { ok: true, message: 'Outlook is connected. Choose which calendars to sync below.' };
    case 'cancelled':
      return { ok: false, message: 'Outlook was not connected: you cancelled on Microsoft.' };
    case 'expired':
      return { ok: false, message: 'Outlook was not connected: that sign-in link expired or was already used. Connect again.' };
    case 'refused':
      return { ok: false, message: 'Outlook was not connected: Microsoft did not grant access.' };
    case 'failed':
      return { ok: false, message: 'Outlook was not connected: something went wrong finishing the sign-in. Try again.' };
    default:
      return null;
  }
}
